import React from "react";
import { Phone, Plus, User } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { CallStatus, LiveCallRow } from "../types";

const STATUS_STYLES: Record<CallStatus, string> = {
  Ringing: "bg-lime-100 text-lime-800 dark:bg-lime-500/15 dark:text-lime-400",
  "On a Call": "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-400",
  "Call Ended": "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400",
  Missed: "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-400",
};

const StatusBadge: React.FC<{ status: CallStatus }> = ({ status }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_STYLES[status]}`}>
    <span className="w-1.5 h-1.5 rounded-full bg-current" /> {status}
  </span>
);

const ChannelNumber: React.FC<{ number: string; channel: "whatsapp" | "phone" }> = ({ number, channel }) => (
  <span className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
    {channel === "whatsapp" ? <FaWhatsapp className="text-green-500 shrink-0" size={14} /> : <Phone className="text-indigo-500 shrink-0" size={14} />}
    {number}
  </span>
);

export const LiveCallsTable: React.FC<{
  calls: LiveCallRow[];
  onViewTranscript: (call: LiveCallRow) => void;
  onStartCall: () => void;
}> = ({ calls, onViewTranscript, onStartCall }) => (
  <section>
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
      <div>
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">Live Calls</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          See a snapshot of the contacts and customer records currently available from your uploaded data.
        </p>
      </div>
      <button
        type="button"
        onClick={onStartCall}
        className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm shrink-0"
      >
        <Plus size={16} /> Start a Call
      </button>
    </div>

    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px] text-left border-collapse">
          <thead>
            <tr className="border-b border-gray-100 dark:border-gray-800">
              {["From", "To", "Agent", "Start Time", "Status", "Duration", "Transcript"].map((h) => (
                <th key={h} className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {calls.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  No calls yet. Start a call to see it show up here in real time.
                </td>
              </tr>
            ) : (
              calls.map((call) => (
                <tr key={call.id} className="border-b border-gray-50 dark:border-gray-800/60 last:border-b-0">
                  <td className="px-4 py-3"><ChannelNumber number={call.from} channel={call.channel} /></td>
                  <td className="px-4 py-3"><ChannelNumber number={call.to} channel={call.channel} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${call.agentType === "ai" ? "bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400" : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"}`}>
                        <User size={12} />
                      </span>
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{call.agentName}</span>
                      {call.agentType === "ai" && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 whitespace-nowrap">
                          AI Agent
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">{call.startTime}</td>
                  <td className="px-4 py-3"><StatusBadge status={call.status} /></td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{call.duration}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {call.hasTranscript ? (
                      <button
                        type="button"
                        onClick={() => onViewTranscript(call)}
                        className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        View Full Transcript
                      </button>
                    ) : (
                      <span className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">No transcripts</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

export default LiveCallsTable;
